import React from "react";
import Modal from "react-bootstrap/Modal";
import { Col, Row } from "react-bootstrap";
import dateFormat from "dateformat";
import {
  eventLabel,
  eventScopeLabel,
  eventSourceLabel,
  renderEventStatus,
} from "../Hook/useEventLogColumns";

/**
 * cr_event_log keeps the request and the response exactly as they went over the wire, so both are
 * text rather than objects. Formatting is best effort: a body that will not parse is shown raw,
 * which is still the thing worth reading when a call went wrong.
 */
const formatPayload = (payload) => {
  if (payload === null || payload === undefined || payload === "") {
    return null;
  }
  try {
    const parsed = JSON.parse(payload);
    // A body serialised on the way in arrives as a quoted JSON string whose text is the real
    // payload; unwrap it once so it does not display as one long escaped line.
    if (typeof parsed === "string") {
      try {
        return JSON.stringify(JSON.parse(parsed), null, 2);
      } catch {
        return parsed;
      }
    }
    return JSON.stringify(parsed, null, 2);
  } catch {
    return payload;
  }
};

const payloadStyle = {
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  maxHeight: "400px",
  overflowY: "auto",
  background: "#f8f9fa",
  border: "1px solid #e9ebec",
  borderRadius: "4px",
  padding: "12px",
  fontSize: "12px",
  margin: 0,
};

/**
 * Shows one side of one logged call - whichever of the two links on the row was clicked.
 *
 * @param mode "request" or "response". The header still carries the event, source, time, scope and
 *             status,
 *             because a payload on its own does not say which call it belonged to.
 */
function EventLogModal({ show, onHide, event, mode }) {
  if (!event) {
    return null;
  }

  const isRequest = mode === "request";
  const payload = formatPayload(
    isRequest ? event.requestData : event.apiResponse
  );

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      backdrop="static"
      keyboard={false}
      className="border-0"
      modalClassName="modal fade zoomIn"
      toggle={onHide}
    >
      <Modal.Header
        className="p-3 bg-info-subtle"
        style={{ fontSize: "large" }}
        closeButton
      >
        {`${eventSourceLabel(event.source)}: ${eventLabel(event.eventType)} - ${
          isRequest ? "Request" : "Response"
        }`}
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-3">
          <Col md={4}>
            <div>
              <span>Date &amp; Time:</span>
            </div>
            <div className="fw-bold">
              {event.createdAt
                ? dateFormat(new Date(event.createdAt), "dd/mm/yyyy hh:MM TT")
                : "-"}
            </div>
          </Col>
          <Col md={4}>
            <div>
              <span>Against:</span>
            </div>
            <div className="fw-bold">
              {eventScopeLabel(event.entityType)}
              {event.entityId ? ` #${event.entityId}` : ""}
            </div>
          </Col>
          <Col md={4}>
            <div>
              <span>Status:</span>
            </div>
            <div className="fw-bold">{renderEventStatus(event.status)}</div>
          </Col>
        </Row>

        {payload ? (
          <pre style={payloadStyle}>{payload}</pre>
        ) : isRequest ? (
          <p>No request recorded</p>
        ) : (
          <p>No response recorded - the call did not come back</p>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default EventLogModal;
